import { getBrowserContextProperties } from './context';
import type {
  EventProperties,
  InitAmplitudeOptions,
  PageViewProperties,
  RevenueProperties,
  UserPropertyValue,
  UserProperties,
} from './types';
import { DEFAULT_PAGE_VIEW_EVENT } from './constants';

let initialized = false;
let enabled = true;
let appName = 'unknown-app';
let envName = 'unknown';
let defaultEventProperties: EventProperties = {};
let isDebug = false;
let eventSamplingRate = 1;
let sdkPromise: Promise<typeof import('@amplitude/analytics-browser')> | null = null;

function loadAmplitudeSdk() {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!sdkPromise) {
    sdkPromise = import('@amplitude/analytics-browser');
  }

  return sdkPromise;
}

function logDebug(message: string, payload?: unknown) {
  if (!isDebug || typeof console === 'undefined') {
    return;
  }

  console.info(`[amplitude] ${message}`, payload ?? '');
}

function getBaseEventProperties(): EventProperties {
  return {
    app_name: appName,
    env: envName,
    ...defaultEventProperties,
    ...getBrowserContextProperties(),
  };
}

function cleanEventProperties(
  properties: EventProperties
): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(properties).filter(
      (entry): entry is [string, string | number | boolean] =>
        typeof entry[1] === 'string' ||
        typeof entry[1] === 'number' ||
        typeof entry[1] === 'boolean'
    )
  );
}

function safelyTrack(eventName: string, properties?: EventProperties) {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  // Explicit local sampling to avoid relying on SDK defaults.
  if (eventSamplingRate < 1 && Math.random() > eventSamplingRate) {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ track }) => {
      track(eventName, { ...getBaseEventProperties(), ...properties });
    })
    .catch((error) => {
      logDebug(`track failed: ${eventName}`, error);
    });
}

function isIdentifyValue(value: UserPropertyValue): boolean {
  if (Array.isArray(value)) {
    return value.every((item) => typeof item === 'string' || typeof item === 'number');
  }

  return ['string', 'number', 'boolean'].includes(typeof value);
}

export function initAmplitude(options: InitAmplitudeOptions) {
  enabled = options.enabled ?? true;
  appName = options.appName;
  envName = options.env ?? 'unknown';
  defaultEventProperties = options.defaultEventProperties ?? {};
  isDebug = options.debug ?? false;
  eventSamplingRate = Math.min(Math.max(options.samplingRate ?? 1, 0), 1);

  if (!enabled || initialized || typeof window === 'undefined') {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ init, setUserId }) => {
      init(options.apiKey, undefined, {
        autocapture: false,
      });

      if (options.userId) {
        setUserId(options.userId);
      }

      initialized = true;
      logDebug('initialized');
    })
    .catch((error) => {
      logDebug('initialization failed', error);
    });
}

export function trackEvent(eventName: string, properties?: EventProperties) {
  safelyTrack(eventName, properties);
}

export function trackPageView(properties?: PageViewProperties) {
  safelyTrack(DEFAULT_PAGE_VIEW_EVENT, properties);
}

export function trackRevenue(properties: RevenueProperties) {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  // Keep revenue events aligned with event sampling behavior.
  if (eventSamplingRate < 1 && Math.random() > eventSamplingRate) {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ Revenue, revenue: trackSdkRevenue }) => {
      const revenuePayload = new Revenue().setEventProperties(
        cleanEventProperties({
          ...getBaseEventProperties(),
          ...properties.eventProperties,
        })
      );

      if (properties.productId !== undefined) {
        revenuePayload.setProductId(properties.productId);
      }
      if (properties.quantity !== undefined) {
        revenuePayload.setQuantity(properties.quantity);
      }
      if (properties.price !== undefined) {
        revenuePayload.setPrice(properties.price);
      }
      if (properties.revenueType !== undefined) {
        revenuePayload.setRevenueType(properties.revenueType);
      }
      if (properties.currency !== undefined) {
        revenuePayload.setCurrency(properties.currency);
      }
      if (properties.revenue !== undefined) {
        revenuePayload.setRevenue(properties.revenue);
      }
      if (properties.receipt !== undefined) {
        revenuePayload.setReceipt(properties.receipt);
      }
      if (properties.receiptSig !== undefined) {
        revenuePayload.setReceiptSig(properties.receiptSig);
      }

      trackSdkRevenue(revenuePayload);
    })
    .catch((error) => {
      logDebug('revenue failed', error);
    });
}

export function identifyUser(userId: string, userProperties?: UserProperties) {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ Identify, identify, setUserId }) => {
      setUserId(userId);

      if (userProperties) {
        const identifyPayload = new Identify();
        Object.entries(userProperties).forEach(([key, value]) => {
          if (value !== undefined) {
            if (isIdentifyValue(value)) {
              identifyPayload.set(key, value);
            }
          }
        });
        identify(identifyPayload);
      }
    })
    .catch((error) => {
      logDebug('identify failed', error);
    });
}

export function setUserProperties(userProperties: UserProperties) {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ Identify, identify }) => {
      const identifyPayload = new Identify();
      Object.entries(userProperties).forEach(([key, value]) => {
        if (value !== undefined) {
          if (isIdentifyValue(value)) {
            identifyPayload.set(key, value);
          }
        }
      });
      identify(identifyPayload);
    })
    .catch((error) => {
      logDebug('set user properties failed', error);
    });
}

/**
 * `$setOnce` — set a user property only the first time it is seen for a user.
 * Use for things like `first_seen_at`, `signup_source`, `first_project_at`.
 * Subsequent calls with the same key are ignored by Amplitude server-side.
 */
export function setUserPropertiesOnce(userProperties: UserProperties) {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ Identify, identify }) => {
      const payload = new Identify();
      Object.entries(userProperties).forEach(([key, value]) => {
        if (value !== undefined && isIdentifyValue(value)) {
          payload.setOnce(key, value);
        }
      });
      identify(payload);
    })
    .catch((error) => {
      logDebug('setOnce failed', error);
    });
}

/**
 * `$add` — atomically increment numeric user properties. Use for counters that
 * may race across tabs/devices (e.g. `total_exports`, `total_projects_created`,
 * `total_ai_generations`). DO NOT compute the new value client-side and `$set`
 * it; that loses concurrent updates.
 */
export function addUserProperties(deltas: Record<string, number>) {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ Identify, identify }) => {
      const payload = new Identify();
      Object.entries(deltas).forEach(([key, delta]) => {
        if (typeof delta === 'number' && Number.isFinite(delta) && delta !== 0) {
          payload.add(key, delta);
        }
      });
      identify(payload);
    })
    .catch((error) => {
      logDebug('add user property failed', error);
    });
}

/**
 * `$append` / `$prepend` — append values to a user-property array (e.g. recent
 * project_ids, recently-used templates). Amplitude keeps last 1000 values.
 */
export function appendUserProperty(key: string, value: UserPropertyValue) {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ Identify, identify }) => {
      if (!isIdentifyValue(value)) return;
      const payload = new Identify();
      payload.append(key, value);
      identify(payload);
    })
    .catch((error) => {
      logDebug('append user property failed', error);
    });
}

/**
 * Group Analytics — associate the current user with one or more groups
 * (e.g. workspace, project). One user can belong to many groups; Amplitude
 * uses this for group-level cohorts (MTU per workspace, plan_tier per team,
 * etc).
 *
 * Pass `null`/`undefined`/`''` to clear membership of a group type.
 */
export function setGroup(groupType: string, groupName: string | string[] | null | undefined) {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ setGroup: sdkSetGroup }) => {
      const value = groupName == null ? '' : Array.isArray(groupName) ? groupName : groupName;
      sdkSetGroup(groupType, value as string | string[]);
    })
    .catch((error) => {
      logDebug('setGroup failed', error);
    });
}

/**
 * Set properties on a Group object (e.g. workspace.plan_tier, project.visibility).
 * These properties live on the GROUP, not the user — different from
 * `setUserProperties`. Used for group-level analytics dashboards.
 */
export function setGroupProperties(
  groupType: string,
  groupName: string,
  properties: UserProperties
) {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ Identify, groupIdentify }) => {
      const payload = new Identify();
      Object.entries(properties).forEach(([key, value]) => {
        if (value !== undefined && isIdentifyValue(value)) {
          payload.set(key, value);
        }
      });
      groupIdentify(groupType, groupName, payload);
    })
    .catch((error) => {
      logDebug('setGroupProperties failed', error);
    });
}

export function resetAmplitude() {
  if (!enabled || typeof window === 'undefined') {
    return;
  }

  void loadAmplitudeSdk()
    ?.then(({ reset }) => {
      reset();
    })
    .catch((error) => {
      logDebug('reset failed', error);
    });
}
